import { throwIfAborted } from '../abort.js';
import { fundingRateIsCurrent, fundingReceiveTargetD6 } from '../amount/fx.js';
import { unitsToDecimal } from '../amount/money.js';
import { isDepositOperation } from '../client.js';
import { operationSettlement } from '../discovery/settlement-asset.js';
import { FundingCurrencyError, unavailableFundingFx, } from '../operations/currency-diagnostics.js';
import { FundingProviderError } from '../provider-error.js';
import { cardPaymentMethod } from './card-payment.js';
import { reviewQuote } from './quote-review.js';
export async function fetchFundingQuote(provider, operation, signal, now, diagnose, purpose = 'execution') {
    throwIfAborted(signal);
    const paymentMethod = purpose === 'indicative'
        ? undefined
        : cardPaymentMethod(operation.method, operation.providerPaymentMethodId);
    if (purpose === 'indicative' &&
        (operation.method !== 'card' || operation.providerPaymentMethodId))
        throw new Error('onramp_card_binding_invalid');
    const target = operation.receiveTarget;
    if (target && operation.settlement.caip19) {
        diagnose?.({
            event: 'onramp.token_quote_rejected',
            currency: operation.fiatCurrency,
            reason: 'unsupported_receive_target',
        });
        throw new Error('onramp_quote_target_asset_unsupported');
    }
    if (target && !fundingRateIsCurrent(target.fx, now ?? Date.now())) {
        diagnose?.(unavailableFundingFx(operation.fiatCurrency, operation.fiatDecimals, target.fx, now ?? Date.now()));
        throw new Error('funding_fx_unavailable');
    }
    if (target && operation.settlement.provider_asset_code !== 'USDC_SOL')
        throw new Error('onramp_quote_target_asset_unsupported');
    const result = await provider.getQuote({
        source: {
            asset: { code: operation.fiatCurrency },
            ...(!target
                ? { amount: unitsToDecimal(BigInt(operation.principalUnits), operation.fiatDecimals) }
                : {}),
        },
        destination: {
            asset: operation.settlement.caip19
                ? { caip19: operation.settlement.caip19 }
                : { code: operation.settlement.provider_asset_code },
            ...(target
                ? {
                    amount: unitsToDecimal(BigInt(fundingReceiveTargetD6(target.localUnits, operation.fiatCurrency, operation.fiatDecimals, target.fx)), 6),
                }
                : {}),
        },
        wallet: { address: operation.destination },
        ...(paymentMethod ? { paymentMethod } : {}),
        feeBehavior: target ? 'inclusive' : 'exclusive',
    });
    throwIfAborted(signal);
    if (!result.ok)
        throw new FundingProviderError(result.error, {
            fiatCurrency: operation.fiatCurrency,
            fiatDecimals: operation.fiatDecimals,
            principalUnits: operation.principalUnits,
            // Provider execution FX/fees cannot be guessed to turn source bounds into receive bounds.
            domain: target ? 'receive' : 'source',
        });
    const quote = result.value.data;
    try {
        const review = reviewQuote(quote, operation, now ?? Date.now(), purpose);
        diagnose?.(review.diagnostic ?? null);
        return { quote, review };
    }
    catch (failure) {
        if (failure instanceof FundingCurrencyError)
            diagnose?.(failure.diagnostic);
        throw failure;
    }
}
export function fundingQuoteAction(options) {
    const { provider, navigation, attemptId, presentQuote, setChallengeUrl } = options;
    return async (current, signal, onReady) => {
        if (!isDepositOperation(current))
            throw new Error('onramp_direction_mismatch');
        const { quote: next, review: checked } = await fetchFundingQuote(provider, {
            ...current,
            ...(options.providerPaymentMethodId
                ? { providerPaymentMethodId: options.providerPaymentMethodId }
                : {}),
            settlement: operationSettlement(current),
        }, signal, undefined, options.diagnose).catch((failure) => {
            if (!signal.aborted && !attemptId)
                navigation.transition('quote_failed');
            throw failure;
        });
        if (checked.state === 'expired')
            throw new Error('quote_expired');
        presentQuote(current.id, next, checked);
        if (checked.challenge && checked.state !== 'limit_exceeded') {
            setChallengeUrl(checked.challenge.url);
            navigation.transition('quote_challenge');
        }
        else if (checked.state === 'ready' && onReady)
            await onReady(next);
        else
            navigation.transition('quote_ready');
    };
}
